// Libraries
import React, { Component, createRef } from 'react';
import Input from './input';
import { translate } from '../translations';

/**
 * Road Reason Select
 *
 * For rendering a text box that looks like a dropdown and letting the user select multiple reasons they selected the road
 *
 * @since 0.5
 * @package /app/globals/form-fields
 */

const reasonOptions = [
    { key: 'issue_pothole', label: translate('issue_pothole') },
    { key: 'issue_unpaved', label: translate('issue_unpaved') },
    { key: 'issue_poor_road', label: translate('issue_poor_road') },
    { key: 'issue_poor_sign', label: translate('issue_poor_sign') },
    { key: 'issue_traffic', label: translate('issue_traffic') },
    { key: 'issue_poor_cycling', label: translate('issue_poor_cycling') },
    { key: 'issue_poor_transit', label: translate('issue_poor_transit') },
    { key: 'issue_poor_walk', label: translate('issue_poor_walk') },
    { key: 'issue_unsafe_cross', label: translate('issue_unsafe_cross') },
    { key: 'other', label: translate('other') },
];

class RoadReasonDropdown extends Component {
    constructor(props) {
        super(props);
        this.state = {
            selectedReasons: [],
            otherText: '',
            dropdownVisible: false,
            inputValue: '',
            highlightedIndex: -1,
        };

        this.handleClickOutside = this.handleClickOutside.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);

        this.reasonInputRef = React.createRef();
        this.reasonDropdownRef = React.createRef();
        this.optionRefs = [];
    }

    componentDidMount() {
        document.addEventListener('click', this.handleClickOutside);
        document.addEventListener('keydown', this.handleKeyDown);
    }

    componentWillUnmount() {
        document.removeEventListener('click', this.handleClickOutside);
        document.removeEventListener('keydown', this.handleKeyDown);
    }

    handleClickOutside() {
        setTimeout(() => {
            if (
                this.reasonDropdownRef.current &&
                !this.reasonDropdownRef.current.contains(
                    document.activeElement
                ) &&
                !this.reasonInputRef.current.contains(document.activeElement)
            ) {
                this.setState({ dropdownVisible: false });
            }
        }, 100);
    }

    handleKeyboardEvent = (event) => {
        const input = document.getElementById('road-reasons');
        const dropdownContainer = document.querySelector(
            'div.form-suggest.road-reasons'
        );

        if (!input) {
            return;
        }

        if (event.key === 'Escape') {
            this.setState({ dropdownVisible: false });
        } else if (event.key === 'Enter') {
            const focusedElement = document.activeElement;

            if (focusedElement.id === input.id) {
                if (!this.state.dropdownVisible) {
                    this.setState({ dropdownVisible: true });
                }
            }

            if (dropdownContainer.contains(focusedElement)) {
                focusedElement.click();
                this.setState({ dropdownVisible: false });
            }
        }
    };

    toggleDropdown = () => {
        this.setState((prevState) => ({
            dropdownVisible: !prevState.dropdownVisible,
            highlightedIndex: -1,
        }));
    };

    handleSelectReason = (key) => {
        this.setState((prevState) => {
            const { selectedReasons } = prevState;
            const index = selectedReasons.indexOf(key);

            const updatedSelectedReasons =
                index > -1
                    ? selectedReasons.filter((reason) => reason !== key)
                    : [...selectedReasons, key];
            const selectedLabels = updatedSelectedReasons
                .map(
                    (reasonKey) =>
                        reasonOptions.find((option) => option.key === reasonKey)
                            .label
                )
                .join(', ');

            let values = [];

            if (updatedSelectedReasons.length > 0) {
                updatedSelectedReasons.map((key) => {
                    const value = reasonOptions.find(
                        (option) => option.key === key
                    );

                    values.push(value.label);
                });
            }

            this.props.onReasonsChange(values);

            return {
                selectedReasons: updatedSelectedReasons,
                inputValue: selectedLabels,
            };
        });
    };

    handleKeyDown(event) {
        const { dropdownVisible, highlightedIndex } = this.state;
        const filteredReasonOptions = reasonOptions.filter(
            (option) =>
                option.key !== 'other' || this.props.provinceCheck === 'sk'
        );

        if (!dropdownVisible) {
            return;
        }

        switch (event.key) {
            case 'ArrowDown':
                this.setState(
                    {
                        highlightedIndex: Math.min(
                            highlightedIndex + 1,
                            filteredReasonOptions.length - 1
                        ),
                    },
                    () => {
                        if (this.optionRefs[this.statventhighlightedIndex]) {
                            this.optionRefs[
                                this.state.highlightedIndex
                            ].focus();
                        }
                    }
                );

                break;
            case 'ArrowUp':
                this.setState(
                    {
                        highlightedIndex: Math.max(highlightedIndex - 1, 0),
                    },
                    () => {
                        if (this.optionRefs[this.state.highlightedIndex]) {
                            this.optionRefs[
                                this.state.highlightedIndex
                            ].focus();
                        }
                    }
                );

                break;
            case 'Tab':
                if (event.shiftKey) {
                    event.preventDefault();

                    this.setState({
                        highlightedIndex: Math.max(highlightedIndex - 1, 0),
                    });
                } else {
                    event.preventDefault();

                    this.setState({
                        highlightedIndex: Math.min(
                            highlightedIndex + 1,
                            filteredReasonOptions.length - 1
                        ),
                    });
                }

                if (this.optionRefs[this.state.highlightedIndex]) {
                    this.optionRefs[this.state.highlightedIndex].focus();
                }

                break;
            case 'Enter':
                if (highlightedIndex >= 0) {
                    const selectedKey =
                        filteredReasonOptions[highlightedIndex].key;

                    this.handleSelectReason(selectedKey);
                }

                break;
            case 'Escape':
                this.setState({ dropdownVisible: false });

                break;
            default:
                break;
        }
    }

    renderClearButton() {
        const { selectedReasons } = this.state;

        if (selectedReasons.length > 0) {
            return (
                <button
                    className="road-reasons-clear-button"
                    onClick={() => this.setState({ selectedReasons: [] })}
                    aria-label={translate('clear_input')}
                >
                    {selectedReasons.length}
                    <span>&times;</span>
                </button>
            );
        }
    }

    renderOptions() {
        const { selectedReasons } = this.state;
        const { provinceCheck } = this.props;

        let filteredReasonOptions = reasonOptions;

        if (provinceCheck !== 'sk') {
            filteredReasonOptions = reasonOptions.filter(
                (option) => option.key !== 'other'
            );
        }

        return (
            <div
                id="road-reasons-dropdown"
                className="form-suggest"
                tabIndex="-1"
                style={{
                    display: this.state.dropdownVisible ? 'block' : 'none',
                }}
                onBlur={this.handleClickOutside}
                ref={this.reasonDropdownRef}
            >
                {filteredReasonOptions.map((option, index) => (
                    <div
                        key={option.key}
                        className={`address-button${
                            selectedReasons.includes(option.key)
                                ? ' selected'
                                : ''
                        }`}
                        onClick={() => this.handleSelectReason(option.key)}
                        tabIndex="0"
                        ref={(el) => (this.optionRefs[index] = el)}
                    >
                        {option.label}
                    </div>
                ))}
            </div>
        );
    }

    render() {
        const { selectedReasons, otherText } = this.state;

        const selectedLabels = selectedReasons
            .map(
                (key) =>
                    reasonOptions.find((option) => option.key === key)?.label
            )
            .join(', ');

        return (
            <div className="form-group road-reason-dropdown">
                <div className="form-group road-reason-input">
                    <Input
                        containerClass="road-reasons-container"
                        inputName="road-reasons-control"
                        inputPlaceholder={translate('select_one_or_more')}
                        readOnly={true}
                        onFocus={this.toggleDropdown}
                        onBlur={this.handleClickOutside}
                        inputClass="form-control pac-target-input"
                        inputType="text"
                        inputDisabled={this.props.inputDisabled}
                        labelText={`${translate('road_unsafe')} ${translate(
                            'optional'
                        )}`}
                        ref={this.reasonInputRef}
                    />
                    {this.renderClearButton()}
                </div>
                {this.renderOptions()}
                <Input
                    containerClass="road-reasons-hidden"
                    inputName="road-reasons"
                    value={selectedLabels}
                    readOnly={true}
                    inputType="hidden"
                />
                {selectedReasons.includes('other') && (
                    <Input
                        containerClass="road-reason-textbox"
                        inputName="road-reason-other"
                        id="road-reason-other"
                        labelText={translate('other_reason')}
                        inputPlaceholder={translate('type_comments')}
                        isRequired={true}
                        value={otherText}
                        type="text"
                        onChange={(e) => {
                            this.props.onOtherReasonsChange(e.target.value);
                            this.setState({ otherText: e.target.value });
                        }}
                    />
                )}
            </div>
        );
    }
}

export default RoadReasonDropdown;
